import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type {
  Broadcast,
  BroadcastCohort,
  CreateBroadcastInput,
  SendBroadcastInput,
  SendBroadcastResult,
  UpdateBroadcastInput,
} from '@/features/broadcasts/broadcasts-types';
import type {
  ProactivenessCohortChannelFilter,
  ProactivenessFilter,
  ProactivenessSection,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';
import type {
  CohortChannelFilters,
  CohortFlagFilters,
  CohortPages,
} from '@/features/im-channel-proactivity-experiments/useImChannelProactivityExperiments';

const PAGE_SIZE = 10;
const QUERY_KEY = ['broadcasts'] as const;

type ApiErrorBody = {
  detail?: string | { user_error_message?: string } | { msg?: string }[];
};

const sections = (filter: ProactivenessFilter): ProactivenessSection[] =>
  filter === 'all' ? ['production', 'experimental'] : [filter];

const cohortQuery = (
  section: ProactivenessSection,
  page: number,
  channel: ProactivenessCohortChannelFilter,
  flagKey: string
) => {
  const params = new URLSearchParams({
    section,
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (channel !== 'all') params.set('channel', channel);
  if (flagKey) params.set('flag_key', flagKey);
  return params.toString();
};

const useBroadcastApi = () => {
  const { getAccessTokenSilently } = useAuth0();
  return async <T>(path: string, fallback: string, init?: RequestInit) => {
    const token = await getAccessTokenSilently();
    const response = await apiFetch(`${API_URL}/admin/broadcasts${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init?.body && { 'Content-Type': 'application/json' }),
      },
    });
    if (!response.ok) {
      const body = (await response
        .json()
        .catch(() => null)) as ApiErrorBody | null;
      const detail = body?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? (detail[0]?.msg ?? fallback)
            : (detail?.user_error_message ?? fallback);
      throw new Error(message);
    }
    return response.json() as Promise<T>;
  };
};

export const useBroadcasts = (section: ProactivenessFilter) => {
  const request = useBroadcastApi();
  return useQuery({
    queryKey: [...QUERY_KEY, section],
    queryFn: async () =>
      (
        await Promise.all(
          sections(section).map((item) =>
            request<Broadcast[]>(
              `?section=${encodeURIComponent(item)}`,
              'Failed to load broadcasts'
            )
          )
        )
      ).flat(),
  });
};

export const useCreateBroadcast = () => {
  const request = useBroadcastApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBroadcastInput) =>
      request<Broadcast>('', 'Failed to create broadcast', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdateBroadcast = () => {
  const request = useBroadcastApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateBroadcastInput }) =>
      request<Broadcast>(`/${id}`, 'Failed to update broadcast', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useBroadcastCohorts = (
  section: ProactivenessFilter,
  pages: CohortPages,
  channelFilters: CohortChannelFilters,
  flagFilters: CohortFlagFilters
) => {
  const request = useBroadcastApi();
  return useQuery({
    queryKey: [
      ...QUERY_KEY,
      section,
      'cohort',
      pages,
      channelFilters,
      flagFilters,
    ],
    placeholderData: (previous) => previous,
    queryFn: () =>
      Promise.all(
        sections(section).map(async (item) => ({
          section: item,
          ...(await request<BroadcastCohort>(
            `/cohort?${cohortQuery(
              item,
              pages[item],
              channelFilters[item],
              flagFilters[item]
            )}`,
            'Failed to load the broadcast cohort'
          )),
        }))
      ),
  });
};

export const useSendBroadcast = () => {
  const request = useBroadcastApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduled_local_at }: SendBroadcastInput) =>
      request<SendBroadcastResult>(`/${id}/send`, 'Failed to send broadcast', {
        method: 'POST',
        body: JSON.stringify({ scheduled_local_at }),
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
